import { useEffect, useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { Icon } from '../ui/Icon';
import { Chip } from '../ui/Chip';
import { fmtEur, fmtDate } from '../../lib/utils';
import { InvoiceType } from '@masamune/shared';
import { Helper } from '../ui/Helper';
import { useOverlayA11y } from '../../lib/overlay';
import { useModalStore } from '../../stores/modal.store';

/**
 * Errore che porta con sé l'id della bozza già creata: serve al recovery
 * idempotente. Se la creazione bozza riesce ma l'EMISSIONE (FIC/SdI/locale)
 * fallisce, NON dobbiamo far ricreare la bozza (= duplicato fiscale): portiamo
 * l'utente alla scheda, dove può ritentare l'emissione sulla bozza esistente.
 */
class EmissionError extends Error {
  constructor(public invoiceId: string, public original: unknown) {
    super(original instanceof Error ? original.message : 'Emissione non riuscita');
  }
}

/**
 * Wizard typeform-style per creare/emettere fatture, note di credito, proforma e avvisi.
 *
 * Step:
 *  1. tipo       — tipologia del documento (4 card grandi)
 *  2. cliente    — chi paga (search + grid)
 *  3. progetto   — collegamento opz. a un progetto del cliente
 *  4. righe      — voci con preset rapidi (consulenza, canone, setup…)
 *  5. date       — emissione + scadenza
 *  6. riepilogo  — preview + CTA "Salva bozza" / "Emetti su FattureInCloud"
 *
 * Coach in-line:
 *  - Avviso se il cliente non ha SDI/PEC per B2B
 *  - Avviso se FIC non è connesso (CTA → Impostazioni)
 */

const TYPE_INFO: Record<InvoiceType, { label: string; sub: string; icon: 'invoice' | 'file' | 'send' | 'bell' }> = {
  FATTURA: {
    label: 'Fattura',
    sub: 'Documento fiscale verso un cliente. Numerazione progressiva.',
    icon: 'invoice',
  },
  NOTA_CREDITO: {
    label: 'Nota di credito',
    sub: 'Storna in tutto o in parte una fattura già emessa.',
    icon: 'file',
  },
  PROFORMA: {
    label: 'Proforma',
    sub: 'Documento non fiscale — utile come pre-fattura o conferma d\'ordine.',
    icon: 'send',
  },
  AVVISO_PAGAMENTO: {
    label: 'Avviso di pagamento',
    sub: 'Promemoria per il cliente — non genera registrazione IVA.',
    icon: 'bell',
  },
};

const PRESET_LINES: Array<{ label: string; line: Line }> = [
  { label: 'Consulenza (h)',     line: { description: 'Consulenza specialistica', quantity: 1, unitPrice: 80,    vatRate: 22 } },
  { label: 'Sviluppo',           line: { description: 'Attività di sviluppo',     quantity: 1, unitPrice: 2500,  vatRate: 22 } },
  { label: 'Setup una tantum',   line: { description: 'Setup iniziale',           quantity: 1, unitPrice: 1500,  vatRate: 22 } },
  { label: 'Canone mensile',     line: { description: 'Canone mensile servizio',  quantity: 1, unitPrice: 250,   vatRate: 22 } },
  { label: 'Hosting',            line: { description: 'Hosting annuale',          quantity: 1, unitPrice: 240,   vatRate: 22 } },
  { label: 'Riga libera',        line: { description: '',                         quantity: 1, unitPrice: 0,     vatRate: 22 } },
];

interface Line {
  description: string;
  quantity: number;
  unitPrice: number;
  vatRate: number;
}

interface ClientLite {
  id: string;
  legalName: string;
  shortName: string;
  vat: string | null;
  sdiCode: string | null;
  pec: string | null;
  paymentTerms: string | null;
}

interface ProjectLite {
  id: string;
  code: string;
  name: string;
  clientId: string;
}

interface FicStatus { connected: boolean; companyName: string | null; }

interface Answers {
  type: InvoiceType;
  clientId: string | null;
  projectId: string | null;
  lines: Line[];
  issueDate: string;
  dueDate: string;
  notes: string;
}

const STEPS = ['tipo', 'cliente', 'progetto', 'righe', 'date', 'riepilogo'] as const;
type StepKey = typeof STEPS[number];

function addDays(iso: string, n: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function termsToDays(t: string | null | undefined): number {
  if (!t) return 30;
  const m = /(\d{1,3})/.exec(t);
  return m ? Number(m[1]) : 30;
}

export function NewInvoiceFlow({
  onClose,
  clientId: initialClientId,
  projectId: initialProjectId,
  type: initialType,
}: {
  onClose: () => void;
  clientId?: string;
  projectId?: string;
  type?: InvoiceType;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const tfRef = useOverlayA11y();
  const closing = useModalStore((s) => s.closing);

  // Skippa cliente se preselezionato
  const initialStepIdx = initialClientId ? 2 : initialType ? 1 : 0;
  const [idx, setIdx] = useState(initialStepIdx);

  // Prefill extra dal payload del modal store (es. azione "Fattura il saldo" dallo stream).
  // ModalsRoot passa già clientId/projectId/type come prop; amount arriva qui direttamente
  // dal payload. È un DEFAULT modificabile (mai lock): semina la prima riga, catturato
  // una sola volta al mount.
  const [prefill] = useState(() => useModalStore.getState().payload);
  const rawAmount = Number(prefill?.amount);
  const presetAmount = Number.isFinite(rawAmount) && rawAmount > 0 ? rawAmount : undefined;

  const [answers, setAnswers] = useState<Answers>({
    type: initialType ?? InvoiceType.FATTURA,
    clientId: initialClientId ?? null,
    projectId: initialProjectId ?? null,
    lines: presetAmount !== undefined
      ? [{ description: 'Saldo progetto', quantity: 1, unitPrice: presetAmount, vatRate: 22 }]
      : [{ description: '', quantity: 1, unitPrice: 0, vatRate: 22 }],
    issueDate: new Date().toISOString().slice(0, 10),
    dueDate: '',
    notes: '',
  });

  const step = STEPS[idx]!;
  const next = () => setIdx(Math.min(idx + 1, STEPS.length - 1));
  const prev = () => setIdx(Math.max(idx - 1, 0));

  // Data fetch
  const { data: clients } = useQuery({
    queryKey: ['clients-lite-invoices'],
    queryFn: () => api.get<{ items: ClientLite[] }>('/clients?pageSize=200'),
  });
  const { data: projects } = useQuery({
    queryKey: ['projects-lite-invoices', answers.clientId],
    queryFn: () => api.get<{ items: ProjectLite[] }>(`/projects?pageSize=100&clientId=${answers.clientId}`),
    enabled: !!answers.clientId,
  });
  const { data: ficStatus } = useQuery({
    queryKey: ['fic-status'],
    queryFn: () => api.get<FicStatus>('/settings/integrations/fic/status'),
  });

  const selectedClient = useMemo(
    () => clients?.items.find((c) => c.id === answers.clientId) ?? null,
    [clients, answers.clientId],
  );

  // Auto-propone la scadenza: si attiva quando cambia cliente O issueDate, ma
  // SOLO se l'utente non ha già manualmente impostato una dueDate.
  // (Se dueDate è già stato editato, non sovrascriviamo la scelta utente.)
  useEffect(() => {
    if (!selectedClient || answers.dueDate) return;
    const days = termsToDays(selectedClient.paymentTerms);
    setAnswers((a) => ({ ...a, dueDate: addDays(a.issueDate, days) }));
  }, [selectedClient, answers.issueDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const subtotal = answers.lines.reduce((a, l) => a + l.quantity * l.unitPrice, 0);
  const vat = answers.lines.reduce((a, l) => a + (l.quantity * l.unitPrice * l.vatRate) / 100, 0);
  const total = subtotal + vat;

  // Keyboard nav
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      // Su step riepilogo Enter NON avanza
      if (e.key === 'Enter' && step !== 'riepilogo') {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        e.preventDefault();
        canAdvance() && next();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [idx, step, answers]); // eslint-disable-line react-hooks/exhaustive-deps

  function canAdvance(): boolean {
    switch (step) {
      case 'tipo': return !!answers.type;
      case 'cliente': return !!answers.clientId;
      case 'progetto': return true; // opz
      case 'righe': return answers.lines.length > 0 && answers.lines.every((l) => l.description.trim() && l.quantity > 0);
      case 'date': return !!answers.issueDate;
      case 'riepilogo': return true;
    }
  }

  // Mutations
  const createDraft = useMutation({
    mutationFn: () => api.post<{ id: string }>('/invoices', {
      type: answers.type,
      clientId: answers.clientId!,
      projectId: answers.projectId,
      issueDate: answers.issueDate,
      dueDate: answers.dueDate || null,
      notes: answers.notes,
      lines: answers.lines,
      status: 'BOZZA',
    }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      toast.ok('Bozza salvata', `Totale € ${fmtEur(total)}`);
      onClose();
      navigate(`/invoices/${res.id}`);
    },
    onError: (e) => toast.alert('Errore', e instanceof ApiError ? e.message : 'Riprova'),
  });

  // Recovery comune: se l'errore è di EMISSIONE (bozza già creata), porta alla
  // scheda per ritentare senza duplicare; altrimenti è un errore di creazione
  // (nessuna bozza creata) → resta nel wizard.
  const onEmitError = (e: unknown) => {
    if (e instanceof EmissionError) {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      toast.warn('Bozza salvata — emissione non riuscita', 'Riprova l\'emissione dalla scheda del documento.');
      onClose();
      navigate(`/invoices/${e.invoiceId}`);
      return;
    }
    toast.alert('Creazione fallita', e instanceof ApiError ? e.message : 'Riprova');
  };

  const createAndPushFic = useMutation({
    mutationFn: async () => {
      const created = await api.post<{ id: string }>('/invoices', {
        type: answers.type,
        clientId: answers.clientId!,
        projectId: answers.projectId,
        issueDate: answers.issueDate,
        dueDate: answers.dueDate || null,
        notes: answers.notes,
        lines: answers.lines,
        status: 'BOZZA',
      });
      try {
        await api.post(`/invoices/${created.id}/push-to-fic`);
      } catch (e) {
        throw new EmissionError(created.id, e);
      }
      return created;
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      toast.ok('Documento emesso su FattureInCloud', `Totale € ${fmtEur(total)}`);
      onClose();
      navigate(`/invoices/${res.id}`);
    },
    onError: onEmitError,
  });

  const createAndIssueLocal = useMutation({
    mutationFn: async () => {
      const created = await api.post<{ id: string }>('/invoices', {
        type: answers.type,
        clientId: answers.clientId!,
        projectId: answers.projectId,
        issueDate: answers.issueDate,
        dueDate: answers.dueDate || null,
        notes: answers.notes,
        lines: answers.lines,
        status: 'BOZZA',
      });
      try {
        await api.post(`/invoices/${created.id}/issue`);
      } catch (e) {
        throw new EmissionError(created.id, e);
      }
      return created;
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      toast.ok('Documento emesso (locale)', `Totale € ${fmtEur(total)}`);
      onClose();
      navigate(`/invoices/${res.id}`);
    },
    onError: onEmitError,
  });

  const pct = ((idx + 1) / STEPS.length) * 100;
  const typeInfo = TYPE_INFO[answers.type];

  return (
    <div className={`tf${closing ? ' tf--leaving' : ''}`} ref={tfRef} role="dialog" aria-modal="true">
      <div className="tf-topbar">
        <div className="flex gap-2.5 items-center">
          <div className="h-[22px] grid place-items-center bg-ink rounded-1 px-2"><img src="/logos/m-white.svg" alt="Masamune" className="h-[9px] w-auto block" /></div>
          <div>
            <div className="font-bold text-[13px] tracking-[0.14em] uppercase">Nuovo documento · {typeInfo.label}</div>
            <div className="mute text-[12px]">Wizard guidato · <span className="t-kbd">Enter</span> per continuare</div>
          </div>
        </div>
        <div className="tf-progress"><span style={{ width: `${pct}%` }} /></div>
        <div className="tf-step-count">{String(idx + 1).padStart(2, '0')} / {String(STEPS.length).padStart(2, '0')}</div>
        <button className="tb-icon-btn" onClick={onClose}><Icon name="x" /></button>
      </div>

      <div className="tf-body">
        <div className="tf-frame tf-enter-anim" key={step}>
          <div className="tf-step-label">
            <span className="num">{String(idx + 1).padStart(2, '0')}</span>
            {step.charAt(0).toUpperCase() + step.slice(1)}
          </div>

          {step === 'tipo' && <TypeStep answers={answers} setAnswers={setAnswers} next={next} />}
          {step === 'cliente' && <ClientStep answers={answers} setAnswers={setAnswers} next={next} clients={clients?.items ?? []} />}
          {step === 'progetto' && <ProjectStep answers={answers} setAnswers={setAnswers} next={next} projects={projects?.items ?? []} />}
          {step === 'righe' && <LinesStep answers={answers} setAnswers={setAnswers} subtotal={subtotal} vat={vat} total={total} />}
          {step === 'date' && <DateStep answers={answers} setAnswers={setAnswers} client={selectedClient} />}
          {step === 'riepilogo' && (
            <SummaryStep
              answers={answers}
              setAnswers={setAnswers}
              client={selectedClient}
              project={projects?.items.find((p) => p.id === answers.projectId) ?? null}
              subtotal={subtotal}
              vat={vat}
              total={total}
              ficStatus={ficStatus}
              onSaveDraft={() => createDraft.mutate()}
              onEmitFic={() => createAndPushFic.mutate()}
              onEmitLocal={() => createAndIssueLocal.mutate()}
              saving={createDraft.isPending || createAndPushFic.isPending || createAndIssueLocal.isPending}
            />
          )}
        </div>
      </div>

      <div className="tf-footer">
        <div className="tf-hint-keys">
          <span><span className="t-kbd">↵</span> Avanti</span>
          <span><span className="t-kbd">Esc</span> Chiudi</span>
        </div>
        <div className="flex gap-2 items-center">
          {step === 'righe' && (
            <span className="mono text-[13px] mute hidden md:inline">€ {fmtEur(total)} totale</span>
          )}
          <button className="btn" onClick={prev} disabled={idx === 0}><Icon name="arrowLeft" size={12} /> Indietro</button>
          {step !== 'riepilogo' && (
            <button className="btn btn-primary" onClick={next} disabled={!canAdvance()}>
              Avanti <Icon name="arrowRight" size={12} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// STEP 1 — Tipo documento
// ════════════════════════════════════════════════════════════════════
function TypeStep({ answers, setAnswers, next }: { answers: Answers; setAnswers: (a: Answers) => void; next: () => void }) {
  return (
    <>
      <h2 className="tf-question">Che tipo di documento vuoi creare?</h2>
      <div className="tf-hint">Il tipo cambia la numerazione e il comportamento fiscale.</div>
      <div className="grid grid-cols-2 gap-3 mt-2">
        {(Object.entries(TYPE_INFO) as Array<[InvoiceType, typeof TYPE_INFO[InvoiceType]]>).map(([k, info], i) => (
          <div
            key={k}
            className="tf-option"
            aria-selected={answers.type === k}
            onClick={() => { setAnswers({ ...answers, type: k }); setTimeout(next, 180); }}
            style={{ padding: '16px 18px' }}
          >
            <span className="tf-option-key"><Icon name={info.icon} size={14} /></span>
            <div className="tf-option-body">
              <span className="font-semibold">{info.label}</span>
              <span className="tf-option-sub">{info.sub}</span>
            </div>
            <span className="mono mute-2 text-[12px]">{String(i + 1)}</span>
          </div>
        ))}
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// STEP 2 — Cliente
// ════════════════════════════════════════════════════════════════════
function ClientStep({ answers, setAnswers, next, clients }: { answers: Answers; setAnswers: (a: Answers) => void; next: () => void; clients: ClientLite[] }) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    if (!q.trim()) return clients.slice(0, 8);
    const needle = q.toLowerCase();
    return clients.filter((c) => c.legalName.toLowerCase().includes(needle) || c.shortName.toLowerCase().includes(needle) || (c.vat && c.vat.includes(needle))).slice(0, 12);
  }, [q, clients]);

  return (
    <>
      <h2 className="tf-question">A chi va intestato?</h2>
      <div className="tf-hint">Cerca un cliente esistente. La sua anagrafica verrà sincronizzata con FattureInCloud automaticamente.</div>
      <div className="mb-3 relative">
        <Icon name="search" size={14} className="absolute left-3 top-3 mute" />
        <input
          className="inp pl-9"
          placeholder="Cerca per ragione sociale o P.IVA…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
        />
      </div>
      <div className="tf-options">
        {filtered.map((c, i) => {
          const missingB2B = !c.sdiCode && !c.pec;
          return (
            <div
              key={c.id}
              className="tf-option"
              aria-selected={answers.clientId === c.id}
              onClick={() => { setAnswers({ ...answers, clientId: c.id }); setTimeout(next, 180); }}
            >
              <span className="tf-option-key">{String.fromCharCode(65 + i)}</span>
              <div className="tf-option-body">
                <span>{c.legalName}</span>
                <span className="tf-option-sub">
                  {c.vat ?? 'P.IVA mancante'}
                  {missingB2B && <> · <span className="text-warn">manca SDI/PEC</span></>}
                </span>
              </div>
              <Icon name="arrowRight" size={14} />
            </div>
          );
        })}
        {filtered.length === 0 && (
          <div className="py-6 text-center mute text-[13px]">
            Nessun cliente trovato. <span className="cursor-pointer" style={{ color: 'var(--accent)' }}>+ Crea anagrafica</span>
          </div>
        )}
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// STEP 3 — Progetto (opz.)
// ════════════════════════════════════════════════════════════════════
function ProjectStep({ answers, setAnswers, next, projects }: { answers: Answers; setAnswers: (a: Answers) => void; next: () => void; projects: ProjectLite[] }) {
  return (
    <>
      <h2 className="tf-question">Collegata a un progetto?</h2>
      <div className="tf-hint">Opzionale — colleghi la fattura al progetto per tracciare ricavi e marginalità.</div>
      <div className="tf-options">
        <div
          className="tf-option"
          aria-selected={answers.projectId === null}
          onClick={() => { setAnswers({ ...answers, projectId: null }); setTimeout(next, 180); }}
        >
          <span className="tf-option-key">—</span>
          <div className="tf-option-body">
            <span>Nessun progetto</span>
            <span className="tf-option-sub">Fattura non collegata (es. consulenza spot, hosting puro)</span>
          </div>
        </div>
        {projects.map((p, i) => (
          <div
            key={p.id}
            className="tf-option"
            aria-selected={answers.projectId === p.id}
            onClick={() => { setAnswers({ ...answers, projectId: p.id }); setTimeout(next, 180); }}
          >
            <span className="tf-option-key mono text-[12px]">{p.code}</span>
            <div className="tf-option-body">
              <span>{p.name}</span>
              <span className="tf-option-sub">Verrà aggiunto al ricavi fatturato del progetto</span>
            </div>
            <Icon name="arrowRight" size={14} />
          </div>
        ))}
        {projects.length === 0 && (
          <div className="py-3 text-center mute text-[13px]">Il cliente non ha progetti aperti — la fattura non sarà collegata a nessun progetto.</div>
        )}
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// STEP 4 — Righe
// ════════════════════════════════════════════════════════════════════
function LinesStep({ answers, setAnswers, subtotal, vat, total }: { answers: Answers; setAnswers: (a: Answers) => void; subtotal: number; vat: number; total: number }) {
  const updateLine = (i: number, patch: Partial<Line>) =>
    setAnswers({ ...answers, lines: answers.lines.map((l, j) => (i === j ? { ...l, ...patch } : l)) });

  const addPreset = (preset: Line) =>
    setAnswers({ ...answers, lines: [...answers.lines.filter((l) => l.description.trim() || l.unitPrice > 0), { ...preset }] });

  const remove = (i: number) =>
    setAnswers({ ...answers, lines: answers.lines.filter((_, j) => j !== i) });

  return (
    <>
      <h2 className="tf-question">Cosa stiamo fatturando?</h2>
      <div className="tf-hint">
        Aggiungi una o più righe. I totali si aggiornano in tempo reale.
        <Helper text="Ogni riga ha quantità × prezzo unitario. L'IVA standard è 22% — usa 10% per prestazioni alimentari/alberghiere, 4% per beni essenziali, 0% per operazioni esenti o reverse charge." />
      </div>

      <div className="space-y-2.5 mb-4">
        {answers.lines.map((l, i) => (
          <div key={i} className="grid items-center gap-2 p-2.5 rounded-1 border border-line hover:bg-paper-2 transition" style={{ gridTemplateColumns: '1fr 70px 110px 70px 100px 30px' }}>
            <input
              className="inp"
              placeholder="Descrizione (es. Sviluppo sito istituzionale)"
              value={l.description}
              onChange={(e) => updateLine(i, { description: e.target.value })}
              autoFocus={i === answers.lines.length - 1 && !l.description}
            />
            <input className="inp mono text-right" type="number" min={0} step="any" value={l.quantity} onChange={(e) => updateLine(i, { quantity: Number(e.target.value) })} title="Quantità" />
            <div className="relative">
              <span className="absolute left-2.5 inset-y-0 flex items-center text-mute text-[13px] pointer-events-none">€</span>
              <input className="inp mono text-right pl-7" type="number" min={0} step="any" value={l.unitPrice} onChange={(e) => updateLine(i, { unitPrice: Number(e.target.value) })} title="Prezzo unitario" />
            </div>
            <select className="inp mono text-right" value={l.vatRate} onChange={(e) => updateLine(i, { vatRate: Number(e.target.value) })} title="IVA">
              <option value={22}>22%</option>
              <option value={10}>10%</option>
              <option value={4}>4%</option>
              <option value={0}>0%</option>
            </select>
            <div className="mono text-right font-semibold">€ {fmtEur(l.quantity * l.unitPrice * (1 + l.vatRate / 100))}</div>
            <button
              type="button"
              className="grid place-items-center w-7 h-7 rounded-1 border border-line text-mute-2 hover:text-alert hover:border-alert hover:bg-alert-bg transition disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-line disabled:hover:bg-transparent disabled:hover:text-mute-2"
              onClick={() => remove(i)}
              disabled={answers.lines.length === 1}
              title={answers.lines.length === 1 ? 'Deve esserci almeno una riga' : 'Elimina riga'}
              aria-label="Elimina riga"
            >
              <Icon name="x" size={12} />
            </button>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <span className="t-micro">Preset rapidi:</span>
        {PRESET_LINES.map((p, i) => (
          <button key={i} className="btn btn-sm btn-dashed" onClick={() => addPreset(p.line)}>
            <Icon name="plus" size={10} /> {p.label}
          </button>
        ))}
      </div>

      <div className="border-t border-line pt-4 mt-2">
        <div className="grid grid-cols-3 gap-3 max-w-[480px] ml-auto">
          <div className="text-right">
            <div className="t-micro">Imponibile</div>
            <div className="mono">€ {fmtEur(subtotal)}</div>
          </div>
          <div className="text-right">
            <div className="t-micro">IVA</div>
            <div className="mono mute">€ {fmtEur(vat)}</div>
          </div>
          <div className="text-right">
            <div className="t-micro">Totale</div>
            <div className="mono text-[20px] font-bold">€ {fmtEur(total)}</div>
          </div>
        </div>
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// STEP 5 — Date
// ════════════════════════════════════════════════════════════════════
function DateStep({ answers, setAnswers, client }: { answers: Answers; setAnswers: (a: Answers) => void; client: ClientLite | null }) {
  const days = termsToDays(client?.paymentTerms);
  const computedDue = answers.issueDate ? addDays(answers.issueDate, days) : '';
  const suggestedDue = computedDue !== answers.dueDate;

  return (
    <>
      <h2 className="tf-question">Quando va emessa e quando scade?</h2>
      <div className="tf-hint">
        Default: oggi · scadenza secondo i termini del cliente
        {client?.paymentTerms && <> (<span className="mono">{client.paymentTerms}</span>)</>}
      </div>
      <div className="grid grid-cols-2 gap-4 max-w-[520px]">
        <div>
          <label className="field-label">
            Data emissione
            <Helper text="Il giorno in cui il documento è prodotto. Generalmente coincide con la consegna del lavoro o l'inizio del servizio." />
          </label>
          <input
            className="inp mono"
            type="date"
            value={answers.issueDate}
            onChange={(e) => setAnswers({ ...answers, issueDate: e.target.value, dueDate: addDays(e.target.value, days) })}
          />
          <div className="field-hint">Il giorno in cui il documento è prodotto.</div>
        </div>
        <div>
          <label className="field-label">
            Scadenza pagamento
            <Helper text="Quando il cliente deve pagare. Viene proposta in automatico in base ai termini del cliente (es. 30/60 giorni data fattura). Puoi cambiarla manualmente." />
          </label>
          <input
            className="inp mono"
            type="date"
            value={answers.dueDate}
            onChange={(e) => setAnswers({ ...answers, dueDate: e.target.value })}
          />
          <div className="field-hint">{suggestedDue && computedDue ? <span className="cursor-pointer" style={{ color: 'var(--accent)' }} onClick={() => setAnswers({ ...answers, dueDate: computedDue })}>Suggerito: {fmtDate(computedDue)}</span> : 'Quando deve arrivare il pagamento.'}</div>
        </div>
      </div>

      <div className="mt-6">
        <label className="field-label">Note (opzionale)</label>
        <textarea
          className="inp"
          rows={3}
          placeholder="Note che appariranno in fondo al documento — es. riferimento ordine, IBAN diverso dal solito, ecc."
          value={answers.notes}
          onChange={(e) => setAnswers({ ...answers, notes: e.target.value })}
        />
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// STEP 6 — Riepilogo + emissione
// ════════════════════════════════════════════════════════════════════
function SummaryStep({
  answers, client, project, subtotal, vat, total, ficStatus,
  onSaveDraft, onEmitFic, onEmitLocal, saving,
}: {
  answers: Answers;
  setAnswers: (a: Answers) => void;
  client: ClientLite | null;
  project: ProjectLite | null;
  subtotal: number; vat: number; total: number;
  ficStatus: FicStatus | undefined;
  onSaveDraft: () => void; onEmitFic: () => void; onEmitLocal: () => void;
  saving: boolean;
}) {
  const typeInfo = TYPE_INFO[answers.type];
  const ficConnected = ficStatus?.connected;
  const missingB2B = client && !client.sdiCode && !client.pec;

  return (
    <>
      <h2 className="tf-question">Tutto pronto — come la vuoi emettere?</h2>
      <div className="tf-hint">Controlla i dati e scegli come procedere. Puoi sempre tornare indietro.</div>

      {/* Coach panels */}
      <div className="space-y-2 mb-4">
        {missingB2B && (
          <CoachPanel tone="warn" icon="alert">
            <strong>Manca SDI/PEC</strong> per <em>{client?.legalName}</em>: se è un cliente B2B, sarà obbligatorio per la fattura elettronica.
            <span className="mute"> Puoi aggiungerli più tardi dalla scheda cliente.</span>
          </CoachPanel>
        )}
        {!ficConnected && (
          <CoachPanel tone="info" icon="link">
            <strong>FattureInCloud non è ancora connesso.</strong> Puoi salvare la bozza ora ed emetterla quando colleghi il token in <em>Impostazioni → Integrazioni</em>.
          </CoachPanel>
        )}
      </div>

      {/* Anteprima documento */}
      <div className="card max-w-[680px]">
        <div className="card-bd">
          <div className="flex justify-between items-start mb-3">
            <div>
              <div className="t-micro">{typeInfo.label}</div>
              <div className=" font-semibold">Bozza · numero assegnato da FIC</div>
            </div>
            <div className="text-right">
              <div className="t-micro">Emissione</div>
              <div className="mono">{fmtDate(answers.issueDate)}</div>
              {answers.dueDate && (
                <>
                  <div className="t-micro mt-1">Scadenza</div>
                  <div className="mono">{fmtDate(answers.dueDate)}</div>
                </>
              )}
            </div>
          </div>

          <div className="border-t border-line pt-3 mt-1">
            <SummaryRow label="Cliente" value={client?.legalName ?? '—'} />
            {client?.vat && <SummaryRow label="P.IVA" value={client.vat} mono />}
            {project && <SummaryRow label="Progetto" value={`${project.code} — ${project.name}`} />}
            {answers.notes && <SummaryRow label="Note" value={answers.notes} />}
          </div>

          <div className="border-t border-line pt-3 mt-3">
            <div className="t-micro mb-2">Righe ({answers.lines.length})</div>
            <table className="w-full text-[13px]">
              <tbody>
                {answers.lines.map((l, i) => (
                  <tr key={i}>
                    <td className="py-1.5">{l.description}</td>
                    <td className="py-1.5 mono mute text-right" style={{ width: 70 }}>{l.quantity} ×</td>
                    <td className="py-1.5 mono text-right" style={{ width: 100 }}>€ {fmtEur(l.unitPrice)}</td>
                    <td className="py-1.5 mono mute text-right" style={{ width: 50 }}>{l.vatRate}%</td>
                    <td className="py-1.5 mono font-semibold text-right" style={{ width: 100 }}>€ {fmtEur(l.quantity * l.unitPrice * (1 + l.vatRate / 100))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="border-t border-line pt-3 mt-3 grid grid-cols-3 gap-3">
            <div className="text-right">
              <div className="t-micro">Imponibile</div>
              <div className="mono">€ {fmtEur(subtotal)}</div>
            </div>
            <div className="text-right">
              <div className="t-micro">IVA</div>
              <div className="mono mute">€ {fmtEur(vat)}</div>
            </div>
            <div className="text-right">
              <div className="t-micro">Totale</div>
              <div className="mono text-[20px] font-bold">€ {fmtEur(total)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-5 flex flex-wrap gap-3 items-center">
        <button className="btn" onClick={onSaveDraft} disabled={saving}>
          <Icon name="file" size={12} /> Salva bozza
        </button>
        {ficConnected ? (
          <button className="btn btn-primary" onClick={onEmitFic} disabled={saving || !client}>
            <Icon name="send" size={12} /> Emetti su FattureInCloud
          </button>
        ) : (
          <button className="btn btn-primary" onClick={onEmitLocal} disabled={saving || !client} title="FIC non connesso — emissione solo locale">
            <Icon name="send" size={12} /> Emetti (locale)
          </button>
        )}
        {ficConnected && (
          <Chip tone="ok" dot><Icon name="link" size={10} /> FIC · {ficStatus?.companyName}</Chip>
        )}
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════
// Util components
// ════════════════════════════════════════════════════════════════════
function SummaryRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between py-1.5 text-[13px]">
      <span className="mute">{label}</span>
      <span className={mono ? 'mono' : 'font-semibold'}>{value}</span>
    </div>
  );
}

function CoachPanel({ tone, icon, children }: { tone: 'info' | 'warn' | 'ok'; icon: 'alert' | 'link' | 'check'; children: React.ReactNode }) {
  // warn/ok = stato semantico reale. info = accento neutro → viola soft (no blu generico).
  const bgClass = tone === 'warn' ? 'bg-warn-bg text-warn' : tone === 'ok' ? 'bg-ok-bg text-ok' : '';
  const softStyle = tone === 'info'
    ? { background: 'color-mix(in oklab, var(--accent) 9%, var(--surface))', color: 'color-mix(in oklab, var(--accent) 72%, var(--ink))' }
    : undefined;
  return (
    <div className={`flex gap-2.5 items-start p-3 rounded-1 text-[13px] ${bgClass}`} style={softStyle}>
      <Icon name={icon} size={14} className="mt-0.5 shrink-0" />
      <div className="leading-[1.5]">{children}</div>
    </div>
  );
}
